import { getScaleFactors, Rect, scaleRect } from "./scaleUtils";
import { createWatermark, applyWatermarkToCanvas } from "./watermark";

export async function generateAnnotationThumbnail({
    mediaElement,
    unscaledAnnotationBox,
    targetWidth = 64,
    targetHeight = 64
}: {
    mediaElement: HTMLImageElement | HTMLVideoElement;
    // The annotation box (x, y are center; width, height are full dimensions)
    // These coordinates must be relative to the *original, unscaled* media dimensions.
    unscaledAnnotationBox: Rect;
    targetWidth?: number;
    targetHeight?: number;
}): Promise<string | null> {
    const sourceCanvas = document.createElement("canvas");
    const sourceCtx = sourceCanvas.getContext("2d");

    if (!sourceCtx) {
        console.error("Failed to get 2D context for source canvas");
        return null;
    }

    let mediaNaturalWidth = 0;
    let mediaNaturalHeight = 0;

    if (mediaElement instanceof HTMLImageElement) {
        if (!mediaElement.complete || mediaElement.naturalWidth === 0) {
            // Wait for image to load if it hasn't
            await new Promise((resolve) => {
                mediaElement.onload = resolve;
                mediaElement.onerror = () => resolve(null); // Resolve with null on error
            });
            if (!mediaElement.complete || mediaElement.naturalWidth === 0) {
                console.error("Image not loaded or has no dimensions for thumbnail generation.");
                return null;
            }
        }
        mediaNaturalWidth = mediaElement.naturalWidth;
        mediaNaturalHeight = mediaElement.naturalHeight;
        sourceCanvas.width = mediaNaturalWidth;
        sourceCanvas.height = mediaNaturalHeight;
        sourceCtx.drawImage(mediaElement, 0, 0, mediaNaturalWidth, mediaNaturalHeight);
    } else if (mediaElement instanceof HTMLVideoElement) {
        if (mediaElement.readyState < 1 || mediaElement.videoWidth === 0) {
            // console.warn("Video not ready for thumbnail generation or has no dimensions.");
            // Attempt to wait for loadedmetadata, but this might not always work if called too early
            if (mediaElement.readyState < mediaElement.HAVE_METADATA) {
                await new Promise((resolve) => {
                    const onLoadedMetadata = () => {
                        mediaElement.removeEventListener("loadedmetadata", onLoadedMetadata);
                        mediaElement.removeEventListener("error", onError);
                        resolve(undefined);
                    };
                    const onError = () => {
                        mediaElement.removeEventListener("loadedmetadata", onLoadedMetadata);
                        mediaElement.removeEventListener("error", onError);
                        resolve(undefined); // or reject
                    };
                    mediaElement.addEventListener("loadedmetadata", onLoadedMetadata);
                    mediaElement.addEventListener("error", onError);
                });
            }
            if (mediaElement.readyState < 1 || mediaElement.videoWidth === 0) {
                console.error(
                    "Video not ready for thumbnail generation or has no dimensions after waiting."
                );
                return null;
            }
        }
        mediaNaturalWidth = mediaElement.videoWidth;
        mediaNaturalHeight = mediaElement.videoHeight;
        sourceCanvas.width = mediaNaturalWidth;
        sourceCanvas.height = mediaNaturalHeight;
        // Ensure video frame is seekable and drawn correctly
        // For more reliability, consider a short pause or seeking if issues persist
        try {
            sourceCtx.drawImage(mediaElement, 0, 0, mediaNaturalWidth, mediaNaturalHeight);
        } catch (e) {
            console.error("Error drawing video frame to canvas for thumbnail:", e);
            return null;
        }
    } else {
        console.error("Unsupported media element type for thumbnail generation.");
        return null;
    }

    if (mediaNaturalWidth === 0 || mediaNaturalHeight === 0) {
        console.error("Media dimensions are zero, cannot generate thumbnail.");
        return null;
    }

    return generateThumbnailFromCanvas({
        sourceCanvas,
        unscaledAnnotationBox,
        mediaNaturalWidth,
        mediaNaturalHeight,
        targetWidth,
        targetHeight
    });
}

// New function to generate thumbnails from image blobs (for server-fetched frames)
export async function generateAnnotationThumbnailFromBlob({
    imageBlob,
    unscaledAnnotationBox,
    targetWidth = 64,
    targetHeight = 64
}: {
    imageBlob: Blob;
    // The annotation box (x, y are center; width, height are full dimensions)
    // These coordinates must be relative to the *original, unscaled* media dimensions.
    unscaledAnnotationBox: Rect;
    targetWidth?: number;
    targetHeight?: number;
}): Promise<string | null> {
    return new Promise((resolve) => {
        const img = new Image();

        img.onload = () => {
            const sourceCanvas = document.createElement("canvas");
            const sourceCtx = sourceCanvas.getContext("2d");

            if (!sourceCtx) {
                console.error("Failed to get 2D context for source canvas");
                URL.revokeObjectURL(img.src);
                resolve(null);
                return;
            }

            sourceCanvas.width = img.naturalWidth;
            sourceCanvas.height = img.naturalHeight;
            sourceCtx.drawImage(img, 0, 0);

            const result = generateThumbnailFromCanvas({
                sourceCanvas,
                unscaledAnnotationBox,
                mediaNaturalWidth: img.naturalWidth,
                mediaNaturalHeight: img.naturalHeight,
                targetWidth,
                targetHeight
            });

            URL.revokeObjectURL(img.src);
            resolve(result);
        };

        img.onerror = () => {
            console.error("Failed to load image from blob for thumbnail generation");
            URL.revokeObjectURL(img.src);
            resolve(null);
        };

        img.src = URL.createObjectURL(imageBlob);
    });
}

// Helper function to generate the actual thumbnail from a canvas
function generateThumbnailFromCanvas({
    sourceCanvas,
    unscaledAnnotationBox,
    mediaNaturalWidth,
    mediaNaturalHeight,
    targetWidth,
    targetHeight
}: {
    sourceCanvas: HTMLCanvasElement;
    unscaledAnnotationBox: Rect;
    mediaNaturalWidth: number;
    mediaNaturalHeight: number;
    targetWidth: number;
    targetHeight: number;
}): string | null {
    // Convert center x, y to top-left x, y for cropping
    const sx = unscaledAnnotationBox.x - unscaledAnnotationBox.width / 2;
    const sy = unscaledAnnotationBox.y - unscaledAnnotationBox.height / 2;
    const sWidth = unscaledAnnotationBox.width;
    const sHeight = unscaledAnnotationBox.height;

    let effectiveSx = sx;
    let effectiveSy = sy;
    let effectiveSWidth = sWidth;
    let effectiveSHeight = sHeight;

    // Ensure crop dimensions are valid and within source bounds
    if (sWidth <= 0 || sHeight <= 0) {
        console.warn("Annotation box for thumbnail has zero or negative dimensions.");
        return null;
    }

    if (sx < 0 || sy < 0 || sx + sWidth > mediaNaturalWidth || sy + sHeight > mediaNaturalHeight) {
        // Note: Silently clamping out-of-bounds annotation boxes for thumbnails

        effectiveSx = Math.max(0, sx);
        effectiveSy = Math.max(0, sy);

        // Adjust width/height based on how much sx/sy were clamped
        // And ensure it doesn't exceed media boundaries from the new sx/sy
        effectiveSWidth = Math.min(sWidth + (sx - effectiveSx), mediaNaturalWidth - effectiveSx);
        effectiveSHeight = Math.min(sHeight + (sy - effectiveSy), mediaNaturalHeight - effectiveSy);

        // If clamping results in non-positive dimensions, then it's fully out or invalid
        if (effectiveSWidth <= 0 || effectiveSHeight <= 0) {
            console.warn(
                "Clamped annotation box for thumbnail results in zero or negative dimensions. No thumbnail will be generated."
            );
            return null;
        }
    }

    const thumbnailCanvas = document.createElement("canvas");
    thumbnailCanvas.width = targetWidth;
    thumbnailCanvas.height = targetHeight;
    const thumbnailCtx = thumbnailCanvas.getContext("2d");

    if (!thumbnailCtx) {
        console.error("Failed to get 2D context for thumbnail canvas");
        return null;
    }

    // Clear canvas to ensure transparency for letterboxing/pillarboxing
    thumbnailCtx.clearRect(0, 0, targetWidth, targetHeight);

    // "object-contain" logic
    const { scaleX, scaleY } = getScaleFactors({
        source: { width: effectiveSWidth, height: effectiveSHeight },
        target: { width: targetWidth, height: targetHeight }
    });

    const scale = Math.min(scaleX, scaleY);

    const drawWidth = effectiveSWidth * scale;
    const drawHeight = effectiveSHeight * scale;

    const drawX = (targetWidth - drawWidth) / 2;
    const drawY = (targetHeight - drawHeight) / 2;

    try {
        thumbnailCtx.drawImage(
            sourceCanvas,
            effectiveSx,
            effectiveSy,
            effectiveSWidth,
            effectiveSHeight,
            drawX,
            drawY,
            drawWidth,
            drawHeight
        );
        return thumbnailCanvas.toDataURL("image/png"); // Use PNG for transparency
    } catch (e) {
        console.error("Error drawing to thumbnail canvas:", e, {
            effectiveSx,
            effectiveSy,
            effectiveSWidth,
            effectiveSHeight,
            drawX,
            drawY,
            drawWidth,
            drawHeight
        });
        return null;
    }
}

/**
 * Downloads the currently visible annotated image by compositing the image and annotation canvas from the DOM.
 * @param opts Optional selectors for image and canvas elements.
 * @returns true if download was triggered, false otherwise
 */
export async function downloadAnnotatedImageFromDOM({
    imageSelector,
    canvasSelector,
    filename,
    includeWatermark,
    watermarkQrUrl
}: {
    imageSelector?: string;
    canvasSelector?: string;
    filename?: string;
    includeWatermark?: boolean;
    watermarkQrUrl?: string;
} = {}): Promise<boolean> {
    // Try to find the image robustly: first by class, then by alt
    let imageEl: HTMLImageElement | null = null;
    if (!imageSelector) {
        // Find all img.absolute, filter for left-1/2 and top-1/2
        const candidates = Array.from(
            document.querySelectorAll("img.absolute")
        ) as HTMLImageElement[];
        imageEl =
            candidates.find(
                (img) => img.classList.contains("left-1/2") && img.classList.contains("top-1/2")
            ) || null;
        // Fallback: try alt="Uploaded file preview"
        if (!imageEl) {
            imageEl = document.querySelector(
                'img[alt="Uploaded file preview"]'
            ) as HTMLImageElement | null;
        }
    } else {
        imageEl = document.querySelector(imageSelector) as HTMLImageElement | null;
    }
    const canvasSelectorToUse = canvasSelector || "canvas[data-annotation-canvas]";
    const filenameToUse =
        filename ||
        `roboflow-annotated-${new Date().toISOString().slice(0, 19).replace(/:/g, "-")}.png`;

    const canvasEl = document.querySelector(canvasSelectorToUse) as HTMLCanvasElement | null;

    if (!imageEl || !canvasEl) {
        console.warn("Could not find image or annotation canvas for download. Tried selectors:", {
            imageEl,
            canvasEl,
            canvasSelector: canvasSelectorToUse
        });
        return false;
    }

    // Create an offscreen canvas with the same size as the image
    const width = imageEl.naturalWidth;
    const height = imageEl.naturalHeight;
    if (!width || !height) {
        console.warn("Image has no natural dimensions.");
        return false;
    }
    const offscreen = document.createElement("canvas");
    offscreen.width = width;
    offscreen.height = height;
    const ctx = offscreen.getContext("2d");
    if (!ctx) {
        console.warn("Could not get 2D context for offscreen canvas.");
        return false;
    }

    // Draw the image
    ctx.drawImage(imageEl, 0, 0, width, height);

    // Draw the annotation canvas, scaling from its displayed size to the image's natural size
    const canvasRect = canvasEl.getBoundingClientRect();
    const imageRect = imageEl.getBoundingClientRect();
    const { scaledRect, offsetX, offsetY } = scaleRect({
        source: imageRect,
        target: offscreen,
        rect: canvasRect,
        offset: true
    });

    ctx.drawImage(
        canvasEl,
        0,
        0,
        canvasEl.width,
        canvasEl.height,
        offsetX as number,
        offsetY as number,
        scaledRect.width,
        scaledRect.height
    );

    // Apply watermark if requested
    if (includeWatermark) {
        const watermarkCanvas = await createWatermark(width, height, { qrUrl: watermarkQrUrl });

        if (watermarkCanvas) {
            applyWatermarkToCanvas(ctx, watermarkCanvas, width, height);
        }
    }

    // Export and trigger download
    offscreen.toBlob((blob) => {
        if (!blob) return;
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = filenameToUse;
        document.body.appendChild(a);
        a.click();
        setTimeout(() => {
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
        }, 100);
    }, "image/png");
    return true;
}
